import { forwardRef, useImperativeHandle } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import { WebView } from '@zenbusiness/application-commons-webview';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import { MODAL_NAMES } from '#client/constants/modals';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useModalContext from '#client/hooks/useModalContext';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';
import usePlaid from '#client/hooks/usePlaid';
import useRedirectToLinkedAccounts, {
  LINK_INTENT
} from '#client/hooks/useRedirectToLinkedAccounts';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import getLastSegmentUrl from '#client/utils/getLastSegmentUrl';
import { linkPlaidWithNativeApp } from '#client/utils/Plaid/getPlaidWebview';

import AddCounterPartyOption from './AddCounterPartyOption';
import { LinkStyled, SecurelyStyled } from '../AddCounterPartyModal.styles';

import { MAIN_ENTRY_PAGES } from '../constants/entryPages';
import { MODAL_TYPES } from '../constants/types';
import { onOpenLinkBankAccountErrorModal } from '../utils/errorHandlers';

const AddBankAndPayee = forwardRef(function AddBankAndPayee(
  {
    handleChangeMode,
    handleOpenManualInfoAddBankModal,
    handleClose,
    refetch,
    entryPageForSendMoney,
    actionsKit
  },
  ref
) {
  const { isWebView } = WebView;
  const { unitAccountId } = useUnitAccountId();
  const captureException = useCaptureException();

  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { redirectToLinkedAccounts } = useRedirectToLinkedAccounts(businessEntityUuid);
  const { openModal } = useModalContext();

  useModalDisplayTracker({
    elementName: 'link_account_modal',
    elementText: 'link_account_modal',
    appPageName: 'link_account_modal'
  });

  const onCounterPartyCreatonError = (errorData) => {
    onOpenLinkBankAccountErrorModal(errorData, openModal);
  };

  const onCounterPartyReceived = (counterParty) => {
    if (typeof refetch === 'function') {
      refetch();
    }

    // Determine the entry page from the current URL.
    const entryPage = getLastSegmentUrl(document.location.pathname);

    // If the flow is triggered from the main entry pages (dashboard or dashboard-notfunded),
    // we'll open the modal and load data on the form.
    // Otherwise, we'll close the current modal and return to the entry page.
    if (entryPage && !MAIN_ENTRY_PAGES.includes(entryPage)) {
      handleClose();
      return;
    }

    if (counterParty) {
      openModal({
        name: MODAL_NAMES.SEND_MONEY_MODAL,
        counterParty,
        entryPageForSendMoney,
        actionsKit
      });
      setTimeout(() => {
        handleClose();
      }, 1000);
      return;
    }

    handleClose();
  };

  const { isPlaidReady, plaid, isHandling, getPlaidLinkToken, isFetchingPlaidToken } = usePlaid({
    unitAccountId,
    businessEntityUuid,
    counterPartyFields: [
      'id',
      'bank',
      'name',
      'accountNumber',
      'accountType',
      'type',
      'status',
      'connectedType',
      'fullAccountNumber',
      'isACHSupported',
      'isWireSupported'
    ],
    onCounterPartyReceived,
    onCounterPartyCreatonError
  });

  useImperativeHandle(
    ref,
    () => ({
      exitPlaid() {
        if (isPlaidReady) {
          plaid.exit();
        }
      }
    }),
    [isPlaidReady, plaid]
  );

  const trackAddBankClick = useButtonPressedCallback({
    elementText: 'Add bank',
    elementName: 'Add_bank',
    appPageName: 'link_account_modal'
  });

  const clickAddBankHandler = () => {
    trackAddBankClick();

    if (redirectToLinkedAccounts({ useTopWindow: true, intent: LINK_INTENT })) {
      return;
    }

    // Handle on webview
    if (isWebView) {
      try {
        const { pathname } = document.location;

        linkPlaidWithNativeApp({
          pathName: pathname,
          unitAccountId,
          modalName: MODAL_NAMES.SEND_MONEY
        });
      } catch (error) {
        captureException(error, {
          level: 'warning'
        });
      }
    } else {
      getPlaidLinkToken();
    }
  };

  const trackAddPayeeClick = useButtonPressedCallback({
    elementText: 'Add payee',
    elementName: 'Add_a_payee',
    appPageName: 'link_account_modal'
  });

  const clickAddPayeeHandler = () => {
    trackAddPayeeClick();
    handleChangeMode(MODAL_TYPES.ADD_PAYEE);
  };

  return (
    <>
      <AddCounterPartyOption
        description="Transfers to & from another account"
        icon={
          <SharedAsset
            alt=""
            height={24}
            src="/s2/icon/temple-fill.svg"
            width={24}
          />
        }
        isDisable={isHandling || isFetchingPlaidToken}
        isLoading={isHandling || isFetchingPlaidToken}
        onClick={clickAddBankHandler}
        title="Add bank"
      />
      <AddCounterPartyOption
        description="Send money to a person or business"
        icon={
          <SharedAsset
            alt=""
            height={24}
            src="/s2/icon/user-fill.svg"
            width={24}
          />
        }
        isDisable={isHandling || isFetchingPlaidToken}
        onClick={clickAddPayeeHandler}
        title="Add payee"
      />
      <Stack
        alignItems="center"
        justifyContent="center"
      >
        <SecurelyStyled
          direction="row"
          spacing="8px"
        >
          <SharedAsset
            alt=""
            height={20}
            src="/s2/icon/lock-outline.svg"
            width={20}
          />
          <Text size="sm">We use Plaid to securely link accounts</Text>
        </SecurelyStyled>
      </Stack>
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="center"
        spacing="8px"
      >
        <Text size="sm">
          <LinkStyled
            color="info"
            onClick={handleOpenManualInfoAddBankModal}
          >
            Can’t find your bank?
          </LinkStyled>
        </Text>
      </Stack>
    </>
  );
});

export default AddBankAndPayee;

AddBankAndPayee.propTypes = {
  handleClose: PropTypes.func,
  refetch: PropTypes.func,
  handleChangeMode: PropTypes.func,
  handleOpenManualInfoAddBankModal: PropTypes.func,
  entryPageForSendMoney: PropTypes.string,
  actionsKit: PropTypes.shape({
    resetSearchParameters: PropTypes.func
  })
};
